import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useSubscription } from "urql";
import Page from "../Page";
import {
  PlayCircle,
  Send,
  Refresh,
  Copy,
  Hash,
  Type,
  Code,
  Close,
} from "../../ui/Icon/Icon";
import urls from "../../routes";
import { getFragmentData, graphql } from "../../__generated__/gql";
import GenerateArticleModal from "../Articles/components/ArticleTable/GenerateArticleModal";
import { useDisclosure } from "../../ui/util/useDisclosure";
import PublishArticleModal from "../Articles/components/ArticleTable/PublishArticleModal";
import PatternName from "../NewPattern/components/PatternName";
import NotFound from "../NotFound/NotFound";
import { ArticleStatus } from "../../__generated__/gql/graphql";
import { useToast } from "../../ui/Toast/ToastContext";
import { useState } from "react";
import Badge from "../../ui/Badge/Badge";
import Loader from "../../ui/Loader/Loader";
import styles from "./Article.module.scss";
import Tabs from "../../ui/Tabs/Tabs";
import ArticleContentBrief from "./ArticleContentBrief";
import ArticleOutline from "./ArticleOutline";
import UnsavedChangesModal from "../../components/UnsavedChangesModal/UnsavedChangesModal";
import CancelArticleGenerationModal from "../Articles/components/ArticleTable/CancelArticleGenerationModal";
import ArticleDetails from "../../components/ArticleEditor/components/ArticleDetails/ArticleDetails";
import ArticleEditor from "../../components/ArticleEditor/ArticleEditor";

const ArticlePageArticle = graphql(/* GraphQL */ `
  fragment ArticlePageArticle on Article {
    id
    title
    version
    versions
    type
    body
    bodyHtml
    bodyText
    copyMarkdown
    copyHtml
    copyText
    excerpt
    metaDescription
    processingMessage
    status
    heroImage {
      ...ArticleImage
    }
    sections {
      ...ArticleSection
    }
    tag {
      id
      name
    }
    pattern {
      id
      templateNodes {
        type
        value
      }
      patternSymbols {
        id
        name
        color
        values
      }
    }
    ...ArticleEditor
    ...ArticleDetails
    ...EditArticleBrief
    ...EditArticleOutline
    ...StatusBadge
  }
`);

const ArticlePageSubscription = graphql(/* GraphQL */ `
  subscription articlePageArticleUpdated {
    articleUpdated {
      ...ArticlePageArticle
    }
  }
`);

const ArticlePageArticleSectionSubscription = graphql(/* GraphQL */ `
  subscription articlePageArticleSectionUpdated {
    articleSectionUpdated {
      ...ArticleSection
    }
  }
`);

const ArticleQuery = graphql(/* GraphQL */ `
  query article($id: String!, $version: Int) {
    article(id: $id, version: $version) {
      ...ArticlePageArticle
    }
    creditsRequired(input: { filters: [{ ids: [$id] }] })
  }
`);

const SetArticleVersionMutation = graphql(/* GraphQL */ `
  mutation setArticleVersion($id: String!, $version: Int!) {
    setArticleVersion(id: $id, version: $version) {
      ...ArticlePageArticle
    }
  }
`);

function Article() {
  const { id } = useParams<{ id: string }>();
  useSubscription({ query: ArticlePageSubscription });
  useSubscription({ query: ArticlePageArticleSectionSubscription });
  const [generateArticleModalOpened, generateArticleModal] = useDisclosure();
  const [publishArticleModalOpened, publishArticleModal] = useDisclosure();
  const [cancelGenerationModalOpened, cancelGenerationModal] = useDisclosure();
  const [setArticleVersionResponse, setArticleVersion] = useMutation(
    SetArticleVersionMutation,
  );
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("editor");
  const [blockNavigation, setBlockNavigation] = useState(false);

  const [articleResponse, refetchArticleQuery] = useQuery({
    query: ArticleQuery,
    variables: { id: id! },
    pause: !id,
    requestPolicy: "network-only",
  });
  const { showToast } = useToast();
  const article = getFragmentData(
    ArticlePageArticle,
    articleResponse.data?.article,
  );

  const creditsRequired = articleResponse.data?.creditsRequired ?? 1;

  if (!article && !articleResponse.fetching) {
    return <NotFound />;
  }

  const breadcrumbs = article?.pattern
    ? [
        { name: "Patterns", url: urls.patterns },
        {
          name: (
            <PatternName
              templateNodes={article.pattern.templateNodes ?? []}
              symbols={article.pattern.patternSymbols ?? []}
            />
          ),
          url: urls.pattern(article.pattern.id),
        },
      ]
    : [{ name: "Articles", url: urls.articles }];

  return (
    <>
      <Page
        className={styles.pageContainer}
        loading={articleResponse.fetching || !articleResponse.data}
        title={
          !articleResponse.fetching
            ? (article?.title ?? (
                <Badge
                  variant="pill"
                  color="default"
                  size="small"
                  showDot={false}
                  loading
                />
              ))
            : null
        }
        documentTitle={article?.title ?? "Article"}
        fullHeight
        breadcrumbs={articleResponse.fetching ? [] : breadcrumbs}
        buttons={
          article
            ? [
                ...(article.version && article.versions > 1
                  ? [
                      {
                        type: "dropdown" as const,
                        variant: "outline" as const,
                        children: `v${article.version}`,
                        loading: setArticleVersionResponse.fetching,
                        items: [
                          {
                            items: Array.from(
                              {
                                length: article.versions,
                              },
                              (v, i) => article.versions - i,
                            ).map((v) => ({
                              label:
                                v === article.version
                                  ? `v${v} (selected)`
                                  : `v${v}`,
                              onClick: async () =>
                                await setArticleVersion({
                                  id: article.id,
                                  version: v,
                                }),
                            })),
                          },
                        ],
                      },
                    ]
                  : []),
                ...(article.body && article.status !== ArticleStatus.Processing
                  ? [
                      {
                        type: "dropdown" as const,
                        leftIcon: <Copy size="medium" />,
                        variant: "outline" as const,
                        children: "Copy",
                        footer: (
                          <div className={styles.copyDropdownFooter}>
                            <Link to={urls.settings.generation.contentOptions}>
                              Manage copy settings
                            </Link>
                          </div>
                        ),
                        items: [
                          {
                            items: [
                              {
                                IconComponent: Type,
                                label: "Copy text",
                                onClick: async () => {
                                  await navigator.clipboard.write([
                                    new ClipboardItem({
                                      "text/plain": new Blob(
                                        [article.copyText!],
                                        {
                                          type: "text/plain",
                                        },
                                      ),
                                      "text/html": new Blob(
                                        [article.copyHtml!],
                                        {
                                          type: "text/html",
                                        },
                                      ),
                                    }),
                                  ]);

                                  showToast({
                                    title: "Copied to clipboard",
                                  });
                                },
                              },
                              {
                                IconComponent: Hash,
                                label: "Copy markdown",
                                onClick: async () => {
                                  await navigator.clipboard.writeText(
                                    article.copyMarkdown!,
                                  );

                                  showToast({
                                    title: "Copied to clipboard",
                                  });
                                },
                              },
                              {
                                IconComponent: Code,
                                label: "Copy HTML",
                                onClick: async () => {
                                  await navigator.clipboard.writeText(
                                    article.copyHtml!,
                                  );
                                  await navigator.clipboard.write([
                                    new ClipboardItem({
                                      "text/plain": new Blob(
                                        [article.copyHtml!],
                                        {
                                          type: "text/plain",
                                        },
                                      ),
                                      "text/html": new Blob(
                                        [article.copyHtml!],
                                        {
                                          type: "text/html",
                                        },
                                      ),
                                    }),
                                  ]);
                                  showToast({
                                    title: "Copied to clipboard",
                                  });
                                },
                              },
                            ],
                          },
                        ],
                      },
                    ]
                  : []),
                ...(article.status !== ArticleStatus.Processing
                  ? [
                      {
                        type: "button" as const,
                        leftIcon: article.body ? (
                          <Refresh size="medium" />
                        ) : (
                          <PlayCircle size="medium" />
                        ),
                        variant: "outline" as const,
                        children: article.body ? "Regenerate" : "Generate",
                        onClick: generateArticleModal.open,
                      },
                    ]
                  : [
                      {
                        type: "button" as const,
                        leftIcon: <Close size="medium" />,
                        variant: "outline" as const,
                        children: "Cancel generation",
                        onClick: cancelGenerationModal.open,
                      },
                    ]),
                {
                  type: "button",
                  leftIcon: <Send size="medium" />,
                  onClick: publishArticleModal.open,
                  variant: "primary",
                  disabled:
                    article.status !== ArticleStatus.Generated &&
                    article.status !== ArticleStatus.Published,
                  children:
                    article.status === ArticleStatus.Published
                      ? "Republish"
                      : "Publish",
                },
              ]
            : undefined
        }
      >
        <UnsavedChangesModal isDirty={blockNavigation} />
        {article ? (
          <>
            <div className={styles.page}>
              {setArticleVersionResponse.fetching ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 32,
                  }}
                >
                  <Loader />
                </div>
              ) : (
                <>
                  <div className={styles.pageLeft}>
                    <Tabs
                      classNames={{
                        list: styles.tabs,
                        root: styles.tabRoot,
                        content: styles.tabContent,
                      }}
                      value={activeTab}
                      onValueChange={setActiveTab}
                      isDirty={blockNavigation}
                      blockIfDirty
                      tabs={[
                        {
                          id: "editor",
                          label: "Editor",
                          content: (
                            <ArticleEditor
                              data={article}
                              openGenerateArticleModal={
                                generateArticleModal.open
                              }
                              setUnsavedChanges={setUnsavedChanges}
                            />
                          ),
                        },
                        {
                          id: "content-brief",
                          label: "Content brief",
                          content: (
                            <ArticleContentBrief
                              data={article}
                              onChange={setBlockNavigation}
                              creditsRequired={creditsRequired}
                            />
                          ),
                        },
                        {
                          id: "outline",
                          label: "Outline",
                          content: (
                            <ArticleOutline
                              data={article}
                              onChange={setBlockNavigation}
                              creditsRequired={creditsRequired}
                            />
                          ),
                        },
                      ]}
                    />
                  </div>
                  <div className={styles.pageRight}>
                    <ArticleDetails
                      article={article}
                      saving={unsavedChanges}
                      onChange={setBlockNavigation}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        ) : null}
      </Page>
      {article && (
        <>
          <GenerateArticleModal
            id={article.id}
            onClose={generateArticleModal.close}
            isOpen={generateArticleModalOpened}
            isDirty={blockNavigation}
            onDiscard={() => {
              refetchArticleQuery();
              setBlockNavigation(false);
            }}
          />
          <PublishArticleModal
            id={article.id}
            onClose={publishArticleModal.close}
            isOpen={publishArticleModalOpened}
          />
          <CancelArticleGenerationModal
            id={article.id}
            onClose={cancelGenerationModal.close}
            isOpen={cancelGenerationModalOpened}
          />
        </>
      )}
    </>
  );
}

export default Article;
